import { PROVENANCE } from '@cyberscore/contracts';
import { Plus, X } from 'lucide-react';
import { useMemo } from 'react';
import {
  describe,
  isGroup,
  needsValue,
  newLeaf,
  omit,
  OP_LABEL,
  opsFor,
  takesDuration,
  takesList,
  update,
  withOp,
  withSubject,
  type Cond,
  type Leaf,
  type Mode,
  type QuestionInfo,
} from './conditions';

/** Facts every assessment has (its context); rules may read deeper ones, typed by hand. */
const FACTS = ['industry', 'country', 'product', 'customer'];

/**
 * Builds a condition without JSON (task 43): tests on questions or facts, combined with "all of",
 * "any of" and "none of". The same editor serves visibility, stage entry, workflows and rules.
 */
export function ConditionEditor({
  value,
  onChange,
  questions,
  mode,
  disabled,
  emptyLabel = 'Always',
}: {
  value: Cond | undefined;
  onChange: (c: Cond | undefined) => void;
  questions: readonly QuestionInfo[];
  mode: Mode;
  disabled?: boolean;
  emptyLabel?: string;
}) {
  const byKey = useMemo(() => new Map(questions.map((q) => [q.key, q])), [questions]);
  const set = (path: (number | 'not')[], f: (c: Cond) => Cond | null) => onChange(value ? (update(value, path, f) ?? undefined) : undefined); // prettier-ignore
  return (
    <div className="cond">
      <div className="cond-reading muted small">{value ? describe(value, byKey) : emptyLabel}</div>
      {value ? (
        <Node c={value} path={[]} set={set} byKey={byKey} questions={questions} mode={mode} disabled={disabled} root />
      ) : (
        !disabled && (
          <button type="button" className="btn btn-sm" onClick={() => onChange(newLeaf(questions[0], mode))}>
            <Plus size={14} /> Add a condition
          </button>
        )
      )}
    </div>
  ); // prettier-ignore
}

type NodeProps = {
  c: Cond;
  path: (number | 'not')[];
  set: (path: (number | 'not')[], f: (c: Cond) => Cond | null) => void;
  byKey: ReadonlyMap<string, QuestionInfo>;
  questions: readonly QuestionInfo[];
  mode: Mode;
  disabled?: boolean;
  root?: boolean;
};

function Node(p: NodeProps) {
  const { c, path, set, questions, mode, disabled } = p;
  // "None of" is stored as not(any(...)).
  const none = 'not' in c && 'any' in c.not;
  if (isGroup(c) || none) {
    const kind = none ? 'none' : 'all' in c ? 'all' : 'any';
    const list = none ? (c as { not: { any: Cond[] } }).not.any : (c as Record<string, Cond[]>)[kind]!;
    const inner: (number | 'not')[] = none ? [...path, 'not'] : path;
    const setKind = (k: string) =>
      set(path, () => (k === 'none' ? { not: { any: list } } : ({ [k]: list } as Cond)));
    const add = (x: Cond) =>
      set(path, () => (none ? { not: { any: [...list, x] } } : ({ [kind]: [...list, x] } as Cond)));
    return (
      <div className="cond-group">
        <div className="cond-head">
          <select className="input input-sm" aria-label="Combine with" disabled={disabled} value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="all">All of these</option>
            <option value="any">Any of these</option>
            <option value="none">None of these</option>
          </select>
          {!disabled && (
            <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label="Remove this group" onClick={() => set(path, () => null)}>
              <X size={14} />
            </button>
          )}
        </div>
        <div className="cond-children">
          {list.map((x, i) => (
            <Node key={i} {...p} root={false} c={x} path={[...inner, i]} />
          ))}
        </div>
        {!disabled && (
          <div className="row gap">
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => add(newLeaf(questions[0], mode))}>
              <Plus size={14} /> Test
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => add({ all: [newLeaf(questions[0], mode)] })}>
              <Plus size={14} /> Group
            </button>
          </div>
        )}
      </div>
    ); // prettier-ignore
  }
  if ('not' in c) {
    return (
      <div className="cond-group">
        <div className="cond-head muted small">Not:</div>
        <Node {...p} root={false} c={c.not} path={[...path, 'not']} />
      </div>
    );
  }
  return (
    <div className="cond-leaf-wrap">
      <LeafEditor {...p} leaf={c as Leaf} />
      {p.root && !disabled && (
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => set(path, (x) => ({ all: [x, newLeaf(questions[0], mode)] }))}>
          <Plus size={14} /> And…
        </button>
      )}
    </div>
  ); // prettier-ignore
}

function LeafEditor({ leaf, path, set, byKey, questions, mode, disabled }: NodeProps & { leaf: Leaf }) {
  const q = leaf.question ? byKey.get(leaf.question) : undefined;
  const ops = opsFor(leaf.fact !== undefined ? null : (q?.type ?? 'text'), mode);
  const put = (l: Leaf) => set(path, () => l);
  const subject = leaf.fact !== undefined ? `fact:${leaf.fact}` : `q:${leaf.question ?? ''}`;
  return (
    <div className="cond-leaf">
      <select
        className="input input-sm subject"
        aria-label="Question or fact"
        disabled={disabled}
        value={FACTS.includes(leaf.fact ?? '') || leaf.fact === undefined ? subject : 'fact:*'}
        onChange={(e) => {
          const v = e.target.value;
          if (v.startsWith('q:')) put(withSubject(leaf, { question: v.slice(2) }, byKey.get(v.slice(2)), mode));
          else put(withSubject(leaf, { fact: v === 'fact:*' ? '' : v.slice(5) }, undefined, mode));
        }}
      >
        {!q && leaf.question !== undefined && <option value={subject}>{leaf.question || 'Choose…'} (unknown)</option>}
        <optgroup label="Questions">
          {questions.map((x) => (
            <option key={x.key} value={`q:${x.key}`}>
              {x.key} · {x.text.length > 60 ? `${x.text.slice(0, 57)}…` : x.text}
            </option>
          ))}
        </optgroup>
        <optgroup label="Facts">
          {FACTS.map((f) => (
            <option key={f} value={`fact:${f}`}>
              {f}
            </option>
          ))}
          <option value="fact:*">Another fact…</option>
        </optgroup>
      </select>
      {leaf.fact !== undefined && !FACTS.includes(leaf.fact) && (
        <input className="input input-sm" aria-label="Fact name" placeholder="twin.operates_ot" disabled={disabled} value={leaf.fact} onChange={(e) => put({ ...leaf, fact: e.target.value })} />
      )}
      <select className="input input-sm" aria-label="Test" disabled={disabled} value={leaf.op} onChange={(e) => put(withOp(leaf, e.target.value, q))}>
        {!ops.includes(leaf.op) && <option value={leaf.op}>{OP_LABEL[leaf.op] ?? leaf.op}</option>}
        {ops.map((o) => (
          <option key={o} value={o}>
            {OP_LABEL[o] ?? o}
          </option>
        ))}
      </select>
      {needsValue(leaf.op) && <ValueInput leaf={leaf} q={q} disabled={disabled} onChange={(value) => put({ ...leaf, value })} />}
      {mode === 'rule' && leaf.question !== undefined && (
        <select
          className="input input-sm"
          aria-label="Counted when it came from"
          disabled={disabled}
          value={leaf.provenance?.[0] ?? ''}
          onChange={(e) => {
            const rest = omit(leaf, 'provenance');
            put(e.target.value ? { ...rest, provenance: [e.target.value] } : rest);
          }}
        >
          <option value="">from any source</option>
          {PROVENANCE.map((p) => (
            <option key={p} value={p}>
              only if {p.toLowerCase().replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      )}
      {!disabled && (
        <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label="Remove this test" onClick={() => set(path, () => null)}>
          <X size={14} />
        </button>
      )}
    </div>
  ); // prettier-ignore
}

function ValueInput({
  leaf,
  q,
  disabled,
  onChange,
}: {
  leaf: Leaf;
  q: QuestionInfo | undefined;
  disabled?: boolean;
  onChange: (v: unknown) => void;
}) {
  // prettier-ignore
  const numeric = q && ['numeric', 'currency', 'percentage', 'employee_count', 'rating'].includes(q.type);
  const date = q && ['date', 'date_range'].includes(q.type);
  const scalar = (v: string): unknown => (numeric ? (v === '' ? '' : Number(v)) : v);
  if (takesDuration(leaf.op))
    return <input className="input input-sm short" aria-label="How long, e.g. 90d" disabled={disabled} value={String(leaf.value ?? '')} onChange={(e) => onChange(e.target.value.trim())} placeholder="90d" />; // prettier-ignore
  if (takesList(leaf.op)) {
    const list = Array.isArray(leaf.value) ? (leaf.value as unknown[]) : [];
    if (q?.options.length)
      return (
        <span className="cond-options">
          {q.options.map((o) => (
            <label key={o.value} className="cond-check">
              <input type="checkbox" disabled={disabled} checked={list.includes(o.value)} onChange={(e) => onChange(e.target.checked ? [...list, o.value] : list.filter((x) => x !== o.value))} /> {o.label}
            </label>
          ))}
        </span>
      ); // prettier-ignore
    return <input className="input input-sm" aria-label="Values, separated by commas" disabled={disabled} value={list.join(', ')} onChange={(e) => onChange(e.target.value.split(',').map((x) => scalar(x.trim())).filter((x) => x !== ''))} />; // prettier-ignore
  }
  if (leaf.op === 'between') {
    const [lo, hi] = Array.isArray(leaf.value) ? (leaf.value as unknown[]) : ['', ''];
    const type = date ? 'date' : numeric ? 'number' : 'text';
    return (
      <span className="row gap">
        <input className="input input-sm short" type={type} aria-label="From" disabled={disabled} value={String(lo ?? '')} onChange={(e) => onChange([scalar(e.target.value), hi])} />
        <span className="muted small">and</span>
        <input className="input input-sm short" type={type} aria-label="To" disabled={disabled} value={String(hi ?? '')} onChange={(e) => onChange([lo, scalar(e.target.value)])} />
      </span>
    ); // prettier-ignore
  }
  if (q?.options.length)
    return (
      <select className="input input-sm" aria-label="Value" disabled={disabled} value={String(leaf.value ?? '')} onChange={(e) => onChange(e.target.value)}>
        {q.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    ); // prettier-ignore
  return (
    <input className="input input-sm short" type={date ? 'date' : numeric ? 'number' : 'text'} aria-label="Value" disabled={disabled} value={String(leaf.value ?? '')} onChange={(e) => onChange(scalar(e.target.value))} /> // prettier-ignore
  );
}
